import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

/*
 * "Go to or act" — the ⌘K palette.
 *
 * It navigates and it runs actions; it does not search content. There is no
 * content-search service behind the platform yet, and a box that looks like
 * search but only matches titles teaches people it is broken. The footer says
 * so plainly rather than leaving them to discover it.
 *
 * Every row comes from the caller's own navigation and permissions, so the
 * palette can never offer a destination their role cannot open. That is the
 * claim the footer makes, and it is the reason the list is passed in rather
 * than assembled here.
 */

export interface Destination {
  label: string;
  desc: string;
  /** A route to go to. Omit for an action that runs in place. */
  path?: string;
  /** An action that runs instead of navigating (settings, a drawer). */
  run?: () => void;
  /** True when this is the screen the person is already on. */
  current?: boolean;
}

export interface DestGroup {
  key: string;
  label: string;
  items: Destination[];
}

interface Props {
  groups: DestGroup[];
  /** Named in the footer: the palette shows only what this role may open. */
  roleLabel: string;
  onClose: () => void;
}

const RECENT_KEY = 'aa_goto_recent';
const RECENT_MAX = 3;

const readRecent = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v) => typeof v === 'string').slice(0, RECENT_MAX);
  } catch {
    return [];
  }
};

const rememberRecent = (label: string) => {
  try {
    const next = [label, ...readRecent().filter((v) => v !== label)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* a browser with storage blocked simply has no recents */
  }
};

const matches = (item: Destination, needle: string): boolean => {
  if (!needle) return true;
  return `${item.label} ${item.desc}`.toLowerCase().includes(needle);
};

export const GoToPalette: React.FC<Props> = ({ groups, roleLabel, onClose }) => {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  /*
   * Recent is what this person picked here before, newest first. It is held in
   * their browser, not on the server: the platform records no "recently
   * visited", and inferring it from their open work would put current work
   * under a heading that says recent.
   */
  const recent = useMemo(() => {
    const flat = groups.flatMap((g) => g.items);
    return readRecent()
      .map((label) => flat.find((i) => i.label === label))
      .filter((i): i is Destination => Boolean(i) && !i!.current);
  }, [groups]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const withRecent = [...groups];
    if (!needle && recent.length > 0) {
      withRecent.push({ key: 'recent', label: 'Recent', items: recent });
    }
    return withRecent
      .map((g) => ({ ...g, items: g.items.filter((i) => matches(i, needle)) }))
      .filter((g) => g.items.length > 0);
  }, [groups, query, recent]);

  const flat = useMemo(() => visible.flatMap((g) => g.items), [visible]);

  useEffect(() => { setCursor(0); }, [query]);

  const go = (target?: Destination) => {
    if (!target) return;
    rememberRecent(target.label);
    onClose();
    if (target.run) {
      target.run();
      return;
    }
    if (target.path) navigate(target.path);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, flat.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(flat[cursor]);
    }
  };

  let index = -1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center bg-ink/25 px-5 pt-[10vh] backdrop-blur-sm"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Go to or act"
        className="flex max-h-[78vh] w-full max-w-[640px] flex-col overflow-hidden rounded-panel border border-line bg-white shadow-[0_24px_64px_rgba(24,25,29,0.22)]"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="shrink-0 px-5 pt-5 sm:px-6 sm:pt-6">
          <div className="flex items-start justify-between gap-4">
            <p className="m-0 text-[11px] font-semibold uppercase leading-[17.4px] tracking-[0.14em] text-eyebrow">
              Go to or act
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-act border border-line-strong bg-white text-ink transition-colors hover:bg-canvas-inset"
            >
              <X size={16} />
            </button>
          </div>
          <h2 className="m-0 mt-2 text-[24px] font-bold leading-[33.2px] tracking-[-0.01em] text-ink">
            Find what you need
          </h2>

          <div className="mt-4 flex items-center gap-2.5 rounded-act border border-line bg-white px-3.5">
            <Search size={17} className="shrink-0 text-ink-soft" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages, Projects, Quick Wins, or actions"
              aria-label="Search destinations and actions"
              className="h-[50px] w-full border-0 bg-transparent p-0 text-[14px] text-ink outline-none placeholder:text-ink-soft focus:ring-0"
            />
            <kbd className="shrink-0 rounded border border-line bg-canvas-inset px-1.5 py-0.5 font-sans text-[10.5px] text-ink-soft">
              esc
            </kbd>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-4 sm:px-3.5">
          {visible.length === 0 && (
            <p className="m-0 px-2.5 py-6 text-center text-[13px] leading-[21.5px] text-ink-dim">
              Nothing here matches that. This moves you between your own screens and
              actions, so it will not find text inside them.
            </p>
          )}

          {visible.map((group) => (
            <div key={group.key} className="mb-4 last:mb-0">
              <p className="m-0 mb-1.5 px-2.5 text-[11px] font-semibold uppercase leading-[17.4px] tracking-[0.14em] text-ink-soft">
                {group.label}
              </p>
              <ul className="m-0 list-none p-0">
                {group.items.map((item) => {
                  index += 1;
                  const active = index === cursor;
                  const mine = index;
                  return (
                    <li key={`${group.key}-${item.label}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setCursor(mine)}
                        onClick={() => go(item)}
                        className={`flex w-full items-center justify-between gap-4 rounded-act px-2.5 py-2.5 text-left transition-colors ${
                          active ? 'bg-canvas-inset' : ''
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[14px] font-semibold leading-[23px] text-ink">
                            {item.label}
                          </span>
                          <span className="block truncate text-[13px] leading-[21.5px] text-ink-dim">
                            {item.desc}
                          </span>
                        </span>
                        {item.current && (
                          <span className="shrink-0 rounded-md bg-canvas-inset px-2 py-1 text-[11.5px] font-semibold leading-none text-ink-muted">
                            Current
                          </span>
                        )}
                        {!item.current && active && (
                          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                            enter
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-line bg-white px-5 py-3.5 sm:px-6">
          <span className="text-[12.5px] leading-[20.75px] text-ink-dim">
            Only destinations allowed for {roleLabel} are shown.
          </span>
          <span className="flex items-center gap-2 text-[12.5px] leading-[20.75px] text-ink-dim">
            <kbd className="rounded border border-line bg-canvas-inset px-1.5 py-0.5 font-sans text-[10.5px] text-ink-soft">⌘K</kbd>
            opens this anywhere in the portal.
          </span>
        </div>
      </div>
    </div>
  );
};

export default GoToPalette;
